import { DocumentType, calculators, entities, selectors } from "@fraction/shared";
import _ from "lodash";
import { useCallback, useMemo, useState } from "react";
import fraction from "src/api/fraction";
import { useBankAccountsQuery, useDocumentsQuery } from "src/apps/PostFundedDashboard/queries";
import { useAuth } from "src/auth";
import { LabeledCheckbox } from "src/components/ui/labeled-checkbox";
import { useModal } from "src/hooks";
import { useApplicationQuery } from "src/hooks/useApplication";
import PlaidLogo from "src/icons/plaid-logo.svg?react";
import { useMutation, useQuery } from "src/lib";
import { usePlaidLink } from "./usePlaidLink";

export function usePlaidForApplication({
  applicationId,
  purpose,
  onSuccess,
  modal = false,
}: {
  applicationId?: string;
  purpose: "underwriting" | "payments";
  modal?: boolean;
  onSuccess?: (accounts: entities.BankAccount[] | undefined) => void;
}) {
  const { refetch: refetchBankAccounts } = useBankAccountsQuery();
  const { showModal } = useModal();
  const [products, setProducts] = useState<("liabilities" | "income_verification" | "assets")[]>([
    "income_verification",
    // "liabilities",
  ]);
  const [productsConnectedToLinkToken, setProductsConnectedToLinkToken] = useState<
    ("liabilities" | "income_verification" | "assets")[]
  >([]);

  const { updateLocalUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [shouldPollBankStatements, setShouldPollBankStatements] = useState(false);
  const {
    refetch,
    data: app,
    isLoading: isLoadingApp,
  } = useApplicationQuery({
    id: applicationId,
    initialRefetch: true,
    accountType: "applicant",
  });

  const {
    refetch: refetchBankStatements,
    data: bankStatements,
    isLoading: isLoadingBankStatements,
  } = useDocumentsQuery(applicationId, {
    initialRefetch: true,
    select: (data) =>
      data.filter((doc) => doc.type?.name === DocumentType.BANKSTMTS && doc?.source === "plaid"),
  });

  // Add polling query to check for bank statements
  useQuery({
    queryKey: ["pollBankStatements", applicationId],
    enabled: !!applicationId && shouldPollBankStatements,
    queryFn: async () => {
      const statements = await refetchBankStatements();
      if (statements?.data?.length) {
        setShouldPollBankStatements(false);
        return statements.data;
      }
      return [];
    },
    retry: true,
    retryDelay: 1000, // Retry every second
    refetchInterval: 1000, // Poll every second
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });

  const debts = useMemo(
    () => (app ? calculators.application.selectAllDebts(app)?.filter((debt) => debt?.plaidItemId) : []),
    [app]
  );
  const assets = useMemo(
    () => (app ? calculators.application.selectAllAssets(app)?.filter((asset) => asset?.plaidItemId) : []),
    [app]
  );
  const applicantIncomes = app ? selectors.application.selectAllIncomesInApplication(app) : [];
  const incomes = useMemo(() => {
    return applicantIncomes?.filter((income) => income?.externalId && income?.externalSource === "plaid");
  }, [applicantIncomes]);

  const registerMutation = useMutation({
    mutationFn: async (token: string) => {
      if (!applicationId) {
        throw new Error("No active application");
      }
      return fraction.registerPlaidBank(token, applicationId, purpose);
    },
    onSuccess: async ({ plaidToken }) => {
      updateLocalUser({ plaidToken });
      setShouldPollBankStatements(true);
      const [bankAccounts] = await Promise.all([refetchBankAccounts(), refetch()]);
      onSuccess?.(bankAccounts?.data);
    },
  });

  const linkTokenMutation = useMutation({
    mutationFn: async ({ products }: { products: ("assets" | "liabilities" | "income_verification")[] }) => {
      const data = await fraction.getPlaidLinkToken(purpose, products);
      // setPlaidLinkToken(data);
      setProductsConnectedToLinkToken(products);
      return data;
    },
    onSuccess: () => {
      setLoading(true);
    },
  });

  const link = usePlaidLink({
    token: linkTokenMutation.data || null,
    onLoad: () => {
      setLoading(false);
    },
    onSuccess: async (public_token, metadata) => {
      // send public_token to server to exchange for access_token
      await registerMutation.mutateAsync(public_token);
    },
    onExit: () => {
      setLoading(false);

      setProductsConnectedToLinkToken([]);
      // setPlaidLinkToken(null);
      linkTokenMutation.reset();
    },
  });

  const startPlaidLink = useMutation({
    mutationFn: async () => {
      if (linkTokenMutation.data && _.isEqual(products, productsConnectedToLinkToken)) {
        await link.open();
      } else {
        if (!products?.length) {
          return;
        }
        setProductsConnectedToLinkToken([]);
        // setPlaidLinkToken(null);
        linkTokenMutation.reset();
        await link.exit();

        await linkTokenMutation.mutateAsync({
          products,
        });
      }
    },
  });

  const start = useCallback(() => {
    if (!modal) {
      return startPlaidLink.mutateAsync();
    }
    showModal({
      header: "Connect your bank account",
      message: (
        <div className="mt-2 mb-6 flex flex-col gap-3">
          <p className="text-sm">
            Connect your bank account to automatically pull account statements and verify your income using
            our partner{" "}
            <a
              className="font-semibold inline-block"
              href="https://plaid.com"
              target="_blank"
              rel="noreferrer"
            >
              Plaid <PlaidLogo width={16} height={16} className="inline-block mb-1" />
            </a>
            .
          </p>
          <b>Choose which information you want to connect.</b>
          <div className="flex flex-row gap-x-6 gap-y-3 flex-wrap">
            <LabeledCheckbox
              label="Income Verification"
              id="income_verification"
              defaultChecked={products.includes("income_verification")}
              onCheckedChange={(checked) => {
                if (checked) {
                  setProducts((prev) => [...prev, "income_verification"]);
                } else {
                  setProducts((prev) => prev.filter((p) => p !== "income_verification"));
                }
              }}
            />

            <LabeledCheckbox
              label="Mortgage(s) and other debts"
              id="liabilities"
              defaultChecked={products.includes("liabilities")}
              onCheckedChange={(checked) => {
                if (checked) {
                  setProducts((prev) => [...prev, "liabilities"]);
                } else {
                  setProducts((prev) => prev.filter((p) => p !== "liabilities"));
                }
              }}
            />
            <i className="text-xs font-normal mt-1">
              Please note by connecting to your bank or credit union, it will pull in information such as your
              account balances, transaction history, and other information.
            </i>
            {/* <LabeledCheckbox
              label="Investment accounts and other assets"
              id="assets"
              defaultChecked={products.includes("assets")}
              onCheckedChange={(checked) => {
                if (checked) {
                  setProducts((prev) => [...prev, "assets"]);
                } else {
                  setProducts((prev) => prev.filter((p) => p !== "assets"));
                }
              }}
            /> */}
          </div>
        </div>
      ),
      actions: [
        {
          text: "Never mind",
          type: "secondary",
        },
        {
          text: "Connect",
          type: "primary",
          action: () => {
            return startPlaidLink.mutateAsync();
          },
        },
      ],
    });
  }, [products, modal]);

  useQuery({
    queryKey: ["linkOpen", linkTokenMutation.data, link.ready],
    enabled: !!linkTokenMutation.data && link.ready,
    queryFn: async () => {
      await link.open();
      return null;
    },
  });

  return {
    ...link,
    ready: !!token,
    isLoading:
      loading || linkTokenMutation.isPending || registerMutation.isPending || startPlaidLink.isPending,
    start: start,
    registerMutation,
    bankStatements,
    isLoadingBankStatements: isLoadingBankStatements || shouldPollBankStatements,
    debts,
    assets,
    incomes,
    isLoadingApp,
  };
}
